import { ArrowLeft, ArrowRight, Check, Image, Loader2, Palette, Plus, RefreshCw, Star, Trash2, Upload, Wand2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { RoomAreaEditor } from '../components/RoomAreaEditor';
import { MessageDialog } from '../components/ui/alert-dialog-helper';
import { useRenderProgress } from '../contexts/RenderProgressContext';
import { useQuoteSocket } from '../hooks/useSocket';
import { RenderUpdateEvent } from '../lib/socket';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  formatCurrency,
  FurnitureRecommendation,
  GenerationParams,
  getPropertyTypeLabel,
  getRoomTypeLabel,
  PropertyType,
  Quote,
  QuoteFloorPlan,
  QuoteRoom,
  RecommendationPackage,
  RenderGenerationStatus,
  RenderStatus,
  RoomType
} from '../types/quote';
import { RENTAL_LENGTH_OPTIONS } from '../types/orderItem';

// Preset types
interface StylePreset {
  key: string;
  label: string;
  description: string;
}

interface LightingPreset {
  key: string;
  label: string;
  description: string;
}

interface CameraPreset {
  key: string;
  label: string;
  description: string;
}

// Preset types
interface StylePreset {
  key: string;
  label: string;
  description: string;
}

interface LightingPreset {
  key: string;
  label: string;
  description: string;
}

interface CameraPreset {
  key: string;
  label: string;
  description: string;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface AssetTemplate {
  id: string;
  name: string;
  description?: string;
  base_price?: number;
  category?: { id: string; name: string };
  images?: Array<{ id: string; url: string }>;
}

const STEPS = ['Customer & Property', 'Floor Plans', 'Rooms & Furniture', 'Review'];

const EditQuote: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = !id;

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Quote data
  const [quote, setQuote] = useState<Partial<Quote>>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    property_name: '',
    property_address: '',
    property_type: undefined,
    total_sqft: undefined,
    bedrooms: undefined,
    rental_length_months: 6,
    total_budget: undefined,
    valid_until: '',
    notes: '',
  });

  const [floorPlans, setFloorPlans] = useState<QuoteFloorPlan[]>([]);
  const [rooms, setRooms] = useState<QuoteRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);

  // Render generation state
  const [stylePresets, setStylePresets] = useState<StylePreset[]>([]);
  const [lightingPresets, setLightingPresets] = useState<LightingPreset[]>([]);
  const [cameraPresets, setCameraPresets] = useState<CameraPreset[]>([]);
  const [generatingRender, setGeneratingRender] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);

  // Global render progress context (for header indicator)
  const { addPendingRender } = useRenderProgress();

  // WebSocket handler for real-time render updates
  const handleRenderUpdate = useCallback((data: RenderUpdateEvent) => {
    console.log('[WebSocket] Render update received:', data);

    // Find the room this render belongs to
    setRooms((prevRooms) => {
      return prevRooms.map((room) => {
        if (room.id !== data.roomId) return room;

        // Update the render in the room's renders array
        const updatedRenders = (room.renders || []).map((render) => {
          if (render.id !== data.renderId) return render;

          return {
            ...render,
            status: data.status === 'completed' ? RenderGenerationStatus.COMPLETED :
                    data.status === 'failed' ? RenderGenerationStatus.FAILED :
                    RenderGenerationStatus.PROCESSING,
            image_url: data.imageUrl || render.image_url,
            error_message: data.error || render.error_message,
          };
        });

        // If render completed, auto-select it if it's the first one
        let selectedRender = room.selected_render;
        if (data.status === 'completed' && data.imageUrl) {
          const completedRender = updatedRenders.find(r => r.id === data.renderId);
          const hasSelectedRender = updatedRenders.some(r => r.is_selected);
          if (completedRender && !hasSelectedRender) {
            selectedRender = completedRender;
          }
        }

        return {
          ...room,
          renders: updatedRenders,
          selected_render: selectedRender,
          render_status: data.status === 'completed' ? RenderStatus.COMPLETED :
                        data.status === 'failed' ? RenderStatus.FAILED : RenderStatus.GENERATING,
          render_error: data.error || undefined,
        };
      });
    });

    // Clear generating state if this was the render we were waiting for
    if (data.status === 'completed' || data.status === 'failed') {
      setGeneratingRender(false);
      if (data.status === 'failed') {
        setRenderError(data.error || 'Render generation failed');
      }
    }
  }, []);

  // Subscribe to WebSocket updates for this quote
  useQuoteSocket(id, handleRenderUpdate);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    if (id) loadQuote();
    loadPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPresets = async () => {
    try {
      const [stylesRes, lightingRes, cameraRes] = await Promise.all([
        fetch('/api/quotes/presets/styles'),
        fetch('/api/quotes/presets/lighting'),
        fetch('/api/quotes/presets/camera'),
      ]);
      const [stylesData, lightingData, cameraData] = await Promise.all([
        stylesRes.json(),
        lightingRes.json(),
        cameraRes.json(),
      ]);
      if (stylesData.success) setStylePresets(stylesData.data || []);
      if (lightingData.success) setLightingPresets(lightingData.data || []);
      if (cameraData.success) setCameraPresets(cameraData.data || []);
    } catch {
      console.error('Failed to load presets');
    }
  };

  const loadQuote = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setQuote(data.data);
        setFloorPlans(data.data.floor_plans || []);
        setRooms(data.data.rooms || []);
      } else {
        setErrorMessage(data.message || 'Failed to load quote');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('An error occurred while loading the quote');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Generate a render for a room
  const handleGenerateRender = async (roomId: string, params: GenerationParams) => {
    if (!id) return;
    try {
      setGeneratingRender(true);
      setRenderError(null);
      console.log('[Render] Starting generation for room:', roomId);
      console.log('[Render] Params:', params);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/rooms/${roomId}/renders`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      const data = await response.json();
      console.log('[Render] Response:', data);

      if (!response.ok) {
        const errorMsg = data.message || `HTTP ${response.status}: ${response.statusText}`;
        console.error('[Render] API error:', errorMsg);
        setRenderError(errorMsg);
        setErrorMessage(errorMsg);
        setErrorDialogOpen(true);
        return;
      }

      if (!data.success) {
        const errorMsg = data.message || 'Failed to generate render';
        console.error('[Render] Generation failed:', errorMsg);
        setRenderError(errorMsg);
        setErrorMessage(errorMsg);
        setErrorDialogOpen(true);
        return;
      }

      // Check if render itself has failed status
      if (data.data?.status === 'failed') {
        const errorMsg = data.data.error_message || 'Render generation failed';
        console.error('[Render] Render failed:', errorMsg);
        setRenderError(errorMsg);
        setErrorMessage(errorMsg);
        setErrorDialogOpen(true);
      }

      // Update the room's renders in state
      const currentRoom = rooms.find(r => r.id === roomId);
      setRooms((prev) =>
        prev.map((room) => {
          if (room.id === roomId) {
            const renders = room.renders || [];
            return { ...room, renders: [...renders, data.data] };
          }
          return room;
        })
      );

      // Add to global pending renders for header indicator (fire-and-forget)
      // WebSocket will handle status updates automatically
      if (data.data.status === RenderGenerationStatus.PENDING ||
          data.data.status === RenderGenerationStatus.PROCESSING) {
        addPendingRender({
          id: data.data.id,
          roomId,
          quoteId: id,
          roomName: currentRoom?.room_name || (currentRoom?.room_type ? getRoomTypeLabel(currentRoom.room_type) : 'Room'),
          quoteName: quote.customer_name || `Quote #${id?.slice(0, 8)}`,
        });
        console.log('[Render] Added to global pending renders - WebSocket will update on completion');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate render';
      console.error('[Render] Exception:', errorMsg);
      setRenderError(errorMsg);
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    } finally {
      setGeneratingRender(false);
    }
  };

  // Select a render as the best one for a room
  const handleSelectRender = async (roomId: string, renderId: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/rooms/${roomId}/renders/${renderId}/select`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        // Update room's selected render
        setRooms((prev) =>
          prev.map((room) => {
            if (room.id === roomId) {
              return {
                ...room,
                selected_render_id: renderId,
                selected_render: room.renders?.find((r) => r.id === renderId),
                renders: room.renders?.map((r) => ({
                  ...r,
                  is_selected: r.id === renderId,
                })),
              };
            }
            return room;
          })
        );
      }
    } catch (err) {
      console.error('Failed to select render:', err);
    }
  };

  // Delete a render
  const handleDeleteRender = async (roomId: string, renderId: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/quotes/${id}/rooms/${roomId}/renders/${renderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Remove from state
      setRooms((prev) =>
        prev.map((room) => {
          if (room.id === roomId) {
            return {
              ...room,
              renders: room.renders?.filter((r) => r.id !== renderId),
            };
          }
          return room;
        })
      );
    } catch (err) {
      console.error('Failed to delete render:', err);
    }
  };

  // Get furniture recommendations for a room
  const handleGetRecommendations = async (
    roomId: string,
    preferences: { style?: string; priority?: string }
  ): Promise<RecommendationPackage[] | null> => {
    if (!id) return null;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/rooms/${roomId}/packages`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
      return null;
    } catch (err) {
      console.error('Failed to get recommendations:', err);
      return null;
    }
  };

  // Accept a recommendation package (add all items to room)
  const handleAcceptPackage = async (roomId: string, items: FurnitureRecommendation[]) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      // Add each item from the package
      for (const item of items) {
        await fetch(`/api/quotes/${id}/rooms/${roomId}/items`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            asset_template_id: item.asset_template_id,
            quantity: item.suggested_quantity,
          }),
        });
      }
      // Reload quote to get updated items
      await loadQuote();
    } catch (err) {
      console.error('Failed to accept package:', err);
    }
  };

  const handleSaveCroppedImage = async (roomId: string, blob: Blob, points: Array<{ x: number; y: number }>) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', blob, 'cropped-room.png');
      formData.append('crop_data', JSON.stringify({ points }));

      const response = await fetch(`/api/quotes/${id}/rooms/${roomId}/cropped-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        // Update the room in local state
        setRooms(rooms.map(r => r.id === roomId ? { ...r, ...data.data } : r));
      } else {
        setErrorMessage(data.message || 'Failed to save cropped image');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      setErrorMessage('Failed to save cropped image');
      setErrorDialogOpen(true);
    }
  };

  const saveQuote = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const method = isCreate ? 'POST' : 'PATCH';
      const url = isCreate ? '/api/quotes' : `/api/quotes/${id}`;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_name: quote.customer_name,
          customer_email: quote.customer_email || null,
          customer_phone: quote.customer_phone || null,
          customer_company: quote.customer_company || null,
          property_name: quote.property_name || null,
          property_address: quote.property_address || null,
          property_type: quote.property_type || null,
          total_sqft: quote.total_sqft || null,
          bedrooms: quote.bedrooms || null,
          rental_length_months: quote.rental_length_months || 6,
          total_budget: quote.total_budget || null,
          valid_until: quote.valid_until || null,
          notes: quote.notes || null,
        }),
      });

      const data = await response.json();
      if (data.success) {
        if (isCreate) {
          navigate(`/quotes/${data.data.id}/edit`, { replace: true });
        } else {
          setQuote(data.data);
        }
        return data.data;
      } else {
        throw new Error(data.message || 'Failed to save quote');
      }
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save quote');
      setErrorDialogOpen(true);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (!quote.customer_name) {
        setErrorMessage('Customer name is required');
        setErrorDialogOpen(true);
        return;
      }
      const saved = await saveQuote();
      if (!saved) return;
    }
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleAddRoom = async () => {
    if (!id) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/rooms`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          room_type: RoomType.LIVING_ROOM,
          budget: 5000,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setRooms([...rooms, data.data]);
        setSelectedRoom(data.data.id);
      } else {
        setErrorMessage(data.message || 'Failed to add room');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('Failed to add room');
      setErrorDialogOpen(true);
    }
  };

  const handleUpdateRoom = async (roomId: string, updates: Partial<QuoteRoom>) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/rooms/${roomId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();
      if (data.success) {
        setRooms(rooms.map((r) => (r.id === roomId ? data.data : r)));
      }
    } catch {
      console.error('Failed to update room');
    }
  };

  const handleDeleteRoom = async (roomId: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/quotes/${id}/rooms/${roomId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setRooms(rooms.filter((r) => r.id !== roomId));
      if (selectedRoom === roomId) {
        setSelectedRoom(rooms.length > 1 ? rooms[0].id : null);
      }
    } catch {
      setErrorMessage('Failed to delete room');
      setErrorDialogOpen(true);
    }
  };

  const handleAddItem = async (roomId: string, templateId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/rooms/${roomId}/items`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ asset_template_id: templateId }),
      });

      const data = await response.json();
      if (data.success) {
        // Reload rooms to get updated items
        loadQuote();
      }
    } catch {
      setErrorMessage('Failed to add item');
      setErrorDialogOpen(true);
    }
  };

  const handleRemoveItem = async (roomId: string, itemId: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/quotes/${id}/rooms/${roomId}/items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      loadQuote();
    } catch {
      setErrorMessage('Failed to remove item');
      setErrorDialogOpen(true);
    }
  };

  const handleFinish = async () => {
    await saveQuote();
    navigate(`/quotes/${id}`);
  };

  const monthlyTotal = rooms.reduce((sum, room) => sum + (room.items_total || 0), 0);
  const rentalMonths = quote.rental_length_months || 6;
  const totalPrice = monthlyTotal * rentalMonths;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="quotes" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="quotes" />
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/quotes')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isCreate ? 'Create Quote' : `Edit ${quote.quote_number}`}
              </h1>
              <p className="text-sm text-muted-foreground">
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep]}
              </p>
            </div>
          </div>

          {/* Progress */}
          <div className="hidden md:flex items-center gap-2">
            {STEPS.map((step, i) => (
              <React.Fragment key={step}>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                    ${i < currentStep ? 'bg-primary text-primary-foreground' : ''}
                    ${i === currentStep ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2' : ''}
                    ${i > currentStep ? 'bg-muted text-muted-foreground' : ''}
                  `}
                >
                  {i < currentStep ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-12 h-0.5 ${i < currentStep ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-8">
          {currentStep === 0 && (
            <CustomerPropertyStep quote={quote} setQuote={setQuote} />
          )}

          {currentStep === 1 && (
            <FloorPlansStep
              quoteId={id || ''}
              floorPlans={floorPlans}
              setFloorPlans={setFloorPlans}
              onError={(msg) => { setErrorMessage(msg); setErrorDialogOpen(true); }}
            />
          )}

          {currentStep === 2 && (
            <RoomsFurnitureStep
              quoteId={id || ''}
              rooms={rooms}
              floorPlans={floorPlans}
              selectedRoom={selectedRoom}
              setSelectedRoom={setSelectedRoom}
              rentalLengthMonths={rentalMonths}
              stylePresets={stylePresets}
              lightingPresets={lightingPresets}
              cameraPresets={cameraPresets}
              generatingRender={generatingRender}
              renderError={renderError}
              onAddRoom={handleAddRoom}
              onUpdateRoom={handleUpdateRoom}
              onDeleteRoom={handleDeleteRoom}
              onAddItem={handleAddItem}
              onRemoveItem={handleRemoveItem}
              onGenerateRender={handleGenerateRender}
              onSelectRender={handleSelectRender}
              onDeleteRender={handleDeleteRender}
              onGetRecommendations={handleGetRecommendations}
              onAcceptPackage={handleAcceptPackage}
              onSaveCroppedImage={handleSaveCroppedImage}
            />
          )}

          {currentStep === 3 && (
            <ReviewStep quote={quote} rooms={rooms} rentalLengthMonths={rentalMonths} monthlyTotal={monthlyTotal} totalPrice={totalPrice} />
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex gap-2">
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={handleNext} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleFinish} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save & Finish
              </Button>
            )}
          </div>
        </div>
      </main>

      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

// Step Components
interface CustomerPropertyStepProps {
  quote: Partial<Quote>;
  setQuote: React.Dispatch<React.SetStateAction<Partial<Quote>>>;
}

const CustomerPropertyStep: React.FC<CustomerPropertyStepProps> = ({ quote, setQuote }) => {
  const handleChange = (field: keyof Quote, value: string | number | undefined) => {
    setQuote((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
          <CardDescription>Enter the customer details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="customer_name">Name *</Label>
            <Input
              id="customer_name"
              value={quote.customer_name || ''}
              onChange={(e) => handleChange('customer_name', e.target.value)}
              placeholder="John Smith"
            />
          </div>
          <div>
            <Label htmlFor="customer_email">Email</Label>
            <Input
              id="customer_email"
              type="email"
              value={quote.customer_email || ''}
              onChange={(e) => handleChange('customer_email', e.target.value)}
              placeholder="john@example.com"
            />
          </div>
          <div>
            <Label htmlFor="customer_phone">Phone</Label>
            <Input
              id="customer_phone"
              value={quote.customer_phone || ''}
              onChange={(e) => handleChange('customer_phone', e.target.value)}
              placeholder="+1 555 123 4567"
            />
          </div>
          <div>
            <Label htmlFor="customer_company">Company</Label>
            <Input
              id="customer_company"
              value={quote.customer_company || ''}
              onChange={(e) => handleChange('customer_company', e.target.value)}
              placeholder="Acme Corp"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Property Information</CardTitle>
          <CardDescription>Enter the property details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="property_name">Property Name</Label>
            <Input
              id="property_name"
              value={quote.property_name || ''}
              onChange={(e) => handleChange('property_name', e.target.value)}
              placeholder="123 Main Street"
            />
          </div>
          <div>
            <Label htmlFor="property_address">Address</Label>
            <Textarea
              id="property_address"
              value={quote.property_address || ''}
              onChange={(e) => handleChange('property_address', e.target.value)}
              placeholder="Full address"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="property_type">Type</Label>
              <Select
                value={quote.property_type || ''}
                onValueChange={(v) => handleChange('property_type', v as PropertyType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(PropertyType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {getPropertyTypeLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bedrooms">Bedrooms</Label>
              <Input
                id="bedrooms"
                type="number"
                min="0"
                value={quote.bedrooms || ''}
                onChange={(e) => handleChange('bedrooms', parseInt(e.target.value) || undefined)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="total_sqft">Square Footage</Label>
              <Input
                id="total_sqft"
                type="number"
                min="0"
                value={quote.total_sqft || ''}
                onChange={(e) => handleChange('total_sqft', parseInt(e.target.value) || undefined)}
              />
            </div>
            <div>
              <Label htmlFor="total_budget">Total Budget</Label>
              <Input
                id="total_budget"
                type="number"
                min="0"
                value={quote.total_budget || ''}
                onChange={(e) => handleChange('total_budget', parseFloat(e.target.value) || undefined)}
                placeholder="£0.00"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="valid_until">Valid Until</Label>
            <Input
              id="valid_until"
              type="date"
              value={quote.valid_until || ''}
              onChange={(e) => handleChange('valid_until', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="rental_length_months">Rental Duration *</Label>
            <Select
              value={(quote.rental_length_months || 6).toString()}
              onValueChange={(value) => handleChange('rental_length_months', parseInt(value))}
            >
              <SelectTrigger id="rental_length_months">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                {RENTAL_LENGTH_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={quote.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Internal notes about this quote..."
            rows={3}
          />
        </CardContent>
      </Card>
    </div>
  );
};

interface FloorPlansStepProps {
  quoteId: string;
  floorPlans: QuoteFloorPlan[];
  setFloorPlans: React.Dispatch<React.SetStateAction<QuoteFloorPlan[]>>;
  onError: (msg: string) => void;
}

const FloorPlansStep: React.FC<FloorPlansStepProps> = ({
  quoteId,
  floorPlans,
  setFloorPlans,
  onError,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !quoteId) return;

    try {
      setUploading(true);

      // Upload the file and create floor plan in one request
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${quoteId}/floor-plans/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setFloorPlans([...floorPlans, data.data]);
      } else {
        throw new Error(data.message || 'Failed to upload floor plan');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (planId: string) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/quotes/${quoteId}/floor-plans/${planId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setFloorPlans(floorPlans.filter((fp) => fp.id !== planId));
    } catch {
      onError('Failed to delete floor plan');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium">Floor Plans</h2>
          <p className="text-sm text-muted-foreground">
            Upload floor plans to help visualize the space
          </p>
        </div>
        <div>
          <input
            type="file"
            id="floor-plan-upload"
            className="hidden"
            accept="image/*"
            onChange={handleUpload}
          />
          <Button asChild disabled={uploading || !quoteId}>
            <label htmlFor="floor-plan-upload" className="cursor-pointer">
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload Floor Plan
            </label>
          </Button>
        </div>
      </div>

      {floorPlans.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {floorPlans.map((fp) => (
            <Card key={fp.id}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base">{fp.name || `Floor ${fp.floor_number}`}</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => handleDelete(fp.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              {fp.image_url && (
                <CardContent>
                  <img
                    src={fp.image_url}
                    alt={fp.name || `Floor ${fp.floor_number}`}
                    className="w-full h-48 object-contain rounded border"
                  />
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No floor plans uploaded yet</p>
            <p className="text-sm">Upload floor plans to mark rooms and generate visualizations</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

interface RoomsFurnitureStepProps {
  quoteId: string;
  rooms: QuoteRoom[];
  floorPlans: QuoteFloorPlan[];
  selectedRoom: string | null;
  setSelectedRoom: (id: string | null) => void;
  rentalLengthMonths: number;
  stylePresets: StylePreset[];
  lightingPresets: LightingPreset[];
  cameraPresets: CameraPreset[];
  generatingRender: boolean;
  renderError: string | null;
  onAddRoom: () => void;
  onUpdateRoom: (roomId: string, updates: Partial<QuoteRoom>) => void;
  onDeleteRoom: (roomId: string) => void;
  onAddItem: (roomId: string, templateId: string) => void;
  onRemoveItem: (roomId: string, itemId: string) => void;
  onGenerateRender: (roomId: string, params: GenerationParams) => void;
  onSelectRender: (roomId: string, renderId: string) => void;
  onDeleteRender: (roomId: string, renderId: string) => void;
  onGetRecommendations: (roomId: string, preferences: { style?: string; priority?: string }) => Promise<RecommendationPackage[] | null>;
  onAcceptPackage: (roomId: string, items: FurnitureRecommendation[]) => void;
  onSaveCroppedImage: (roomId: string, blob: Blob, points: Array<{ x: number; y: number }>) => Promise<void>;
}

// Room type filtering is now handled by the backend API via room_type parameter

const RoomsFurnitureStep: React.FC<RoomsFurnitureStepProps> = ({
  quoteId,
  rooms,
  floorPlans,
  selectedRoom,
  setSelectedRoom,
  rentalLengthMonths,
  stylePresets,
  lightingPresets,
  cameraPresets,
  generatingRender,
  renderError,
  onAddRoom,
  onUpdateRoom,
  onDeleteRoom,
  onAddItem,
  onRemoveItem,
  onGenerateRender,
  onSelectRender,
  onDeleteRender,
  onGetRecommendations,
  onAcceptPackage,
  onSaveCroppedImage,
}) => {
  const getRentalLabel = (months: number) =>
    RENTAL_LENGTH_OPTIONS.find((opt) => opt.value === months)?.label || `${months} months`;
  const currentRoom = rooms.find((r) => r.id === selectedRoom);

  // Furniture state with infinite scroll
  const [furniture, setFurniture] = useState<AssetTemplate[]>([]);
  const [furniturePage, setFurniturePage] = useState(1);
  const [hasMoreFurniture, setHasMoreFurniture] = useState(true);
  const [loadingFurniture, setLoadingFurniture] = useState(false);
  const [furnitureSearch, setFurnitureSearch] = useState('');
  const furnitureContainerRef = React.useRef<HTMLDivElement>(null);
  const loadMoreRef = React.useRef<HTMLDivElement>(null);

  // Load furniture with pagination, filtered by room type via backend
  const loadFurniture = React.useCallback(async (page: number, reset: boolean = false, roomType?: string) => {
    if (loadingFurniture) return;
    setLoadingFurniture(true);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(page),
        limit: '20',
        is_active: 'true',
      });
      if (furnitureSearch) params.append('search', furnitureSearch);
      // Pass room_type to backend for filtering by category mappings
      if (roomType) params.append('room_type', roomType);

      const response = await fetch(`/api/asset-templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        const newItems = data.data || [];
        setFurniture(prev => reset ? newItems : [...prev, ...newItems]);
        setHasMoreFurniture(newItems.length === 20);
        setFurniturePage(page);
      }
    } catch (err) {
      console.error('Failed to load furniture:', err);
    } finally {
      setLoadingFurniture(false);
    }
  }, [furnitureSearch, loadingFurniture]);

  // Reload furniture when room type changes or search changes
  useEffect(() => {
    loadFurniture(1, true, currentRoom?.room_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [furnitureSearch, currentRoom?.room_type]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreFurniture && !loadingFurniture) {
          loadFurniture(furniturePage + 1, false, currentRoom?.room_type);
        }
      },
      { threshold: 0.1 }
    );

    const el = loadMoreRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [hasMoreFurniture, loadingFurniture, furniturePage, loadFurniture, currentRoom?.room_type]);

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState<'manual' | 'auto'>('manual');
  const [autoStylePreference, setAutoStylePreference] = useState<string>('modern');
  const [autoPriority, setAutoPriority] = useState<string>('best_match');
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [packages, setPackages] = useState<RecommendationPackage[] | null>(null);
  const [selectedPackageTier, setSelectedPackageTier] = useState<'budget' | 'standard' | 'premium'>('standard');

  // Render generation form state
  const [selectedStyle, setSelectedStyle] = useState<string>('modern');
  const [selectedLighting, setSelectedLighting] = useState<string>('natural');
  const [selectedCamera, setSelectedCamera] = useState<string>('wide');
  const [selectedFloorType, setSelectedFloorType] = useState<string>('hardwood');
  const [selectedWallColor, setSelectedWallColor] = useState<string>('white');
  const [customPrompt, setCustomPrompt] = useState<string>('');

  // Lightbox state
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Fetch recommendations when switching to auto mode
  const handleGenerateSuggestions = async () => {
    if (!currentRoom) return;
    setLoadingRecommendations(true);
    const result = await onGetRecommendations(currentRoom.id, {
      style: autoStylePreference,
      priority: autoPriority,
    });
    setPackages(result);
    setLoadingRecommendations(false);
  };

  // Get selected package
  const selectedPackage = packages?.find((p) => p.tier === selectedPackageTier);

  // Get floor plan for current room
  const currentFloorPlan = currentRoom?.floor_plan_id
    ? floorPlans.find((fp) => fp.id === currentRoom.floor_plan_id)
    : floorPlans[0]; // Default to first floor plan

  // Handle generate render
  const handleGenerate = () => {
    if (!currentRoom) return;
    onGenerateRender(currentRoom.id, {
      style: [selectedStyle],
      lighting: selectedLighting,
      camera_angle: selectedCamera,
      floor_type: selectedFloorType,
      wall_color: selectedWallColor,
      custom_instructions: customPrompt || undefined,
    });
  };

  // Furniture is now pre-filtered by the backend based on room_type
  // No client-side filtering needed

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Room List */}
      <div className="lg:col-span-1">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">Rooms</h2>
          <Button size="sm" onClick={onAddRoom}>
            <Plus className="h-4 w-4 mr-1" />
            Add Room
          </Button>
        </div>

        <div className="space-y-2">
          {rooms.map((room) => (
            <Card
              key={room.id}
              className={`cursor-pointer transition-colors ${
                selectedRoom === room.id ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedRoom(room.id)}
            >
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{room.room_name || getRoomTypeLabel(room.room_type)}</p>
                    <p className="text-sm text-muted-foreground">
                      {room.items_count || 0} items • {formatCurrency(room.items_total || 0)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteRoom(room.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {rooms.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No rooms yet. Click "Add Room" to start.
            </p>
          )}
        </div>
      </div>

      {/* Room Editor */}
      <div className="lg:col-span-2">
        {currentRoom ? (
          <div className="space-y-6">
            {/* Room Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Room Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Room Type</Label>
                  <Select
                    value={currentRoom.room_type}
                    onValueChange={(v) => onUpdateRoom(currentRoom.id, { room_type: v as RoomType })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(RoomType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {getRoomTypeLabel(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Custom Name</Label>
                  <Input
                    value={currentRoom.room_name || ''}
                    onChange={(e) => onUpdateRoom(currentRoom.id, { room_name: e.target.value })}
                    placeholder="e.g., Master Suite"
                  />
                </div>
                <div>
                  <Label>Budget</Label>
                  <Input
                    type="number"
                    min="0"
                    value={currentRoom.budget || ''}
                    onChange={(e) => onUpdateRoom(currentRoom.id, { budget: parseFloat(e.target.value) || undefined })}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <Label>Dimensions (W × L ft)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={currentRoom.width_ft || ''}
                      onChange={(e) => onUpdateRoom(currentRoom.id, { width_ft: parseFloat(e.target.value) || undefined })}
                      placeholder="Width"
                    />
                    <Input
                      type="number"
                      min="0"
                      value={currentRoom.length_ft || ''}
                      onChange={(e) => onUpdateRoom(currentRoom.id, { length_ft: parseFloat(e.target.value) || undefined })}
                      placeholder="Length"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Room Area - Polygon Cropping */}
            <Card>
              <CardHeader>
                <CardTitle>Room Area</CardTitle>
                <CardDescription>
                  Define the room boundaries on the floor plan for AI rendering
                </CardDescription>
              </CardHeader>
              <CardContent>
                {floorPlans.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No floor plans uploaded yet.</p>
                    <p className="text-sm">Upload a floor plan first to define room areas.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Floor Plan Selector */}
                    <div>
                      <Label>Select Floor Plan</Label>
                      <Select
                        value={currentRoom.floor_plan_id || ''}
                        onValueChange={(v) => onUpdateRoom(currentRoom.id, { floor_plan_id: v || undefined })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a floor plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {floorPlans.map((fp) => (
                            <SelectItem key={fp.id} value={fp.id}>
                              {fp.name || `Floor ${fp.floor_number}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Polygon Editor */}
                    {currentRoom.floor_plan_id && (() => {
                      const selectedFloorPlan = floorPlans.find(fp => fp.id === currentRoom.floor_plan_id);
                      if (!selectedFloorPlan?.image_url) return null;

                      return (
                        <RoomAreaEditor
                          floorPlanUrl={selectedFloorPlan.image_url}
                          initialPoints={currentRoom.crop_data && 'points' in currentRoom.crop_data ? currentRoom.crop_data.points : []}
                          onPointsChange={(points) => {
                            // Optionally save points as user draws
                          }}
                          onCrop={(blob, points) => {
                            onSaveCroppedImage(currentRoom.id, blob, points);
                          }}
                        />
                      );
                    })()}

                    {/* Show existing cropped image if available */}
                    {currentRoom.cropped_image_url && (
                      <div className="mt-4">
                        <Label className="mb-2 block">Current Room Crop</Label>
                        <div className="border rounded-lg overflow-hidden inline-block">
                          <img
                            src={currentRoom.cropped_image_url}
                            alt="Cropped room"
                            className="max-w-full max-h-32"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Furniture Selection */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Furniture</CardTitle>
                    <CardDescription>
                      Current: {currentRoom.items?.length || 0} items • {formatCurrency((currentRoom.items_total || 0) * rentalLengthMonths)} ({getRentalLabel(rentalLengthMonths)})
                      {currentRoom.budget && (
                        <span className={(currentRoom.items_total || 0) * rentalLengthMonths > currentRoom.budget ? ' text-red-500' : ''}>
                          {' '}/ {formatCurrency(currentRoom.budget)} budget
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  {/* Mode Toggle */}
                  <div className="flex gap-1 bg-muted rounded-lg p-1">
                    <Button
                      variant={selectionMode === 'manual' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSelectionMode('manual')}
                    >
                      Manual
                    </Button>
                    <Button
                      variant={selectionMode === 'auto' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setSelectionMode('auto')}
                    >
                      <Wand2 className="h-4 w-4 mr-1" />
                      Auto-Select
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Current Items - shown in both modes */}
                {currentRoom.items && currentRoom.items.length > 0 && (
                  <div className="mb-4">
                    <div className="space-y-2">
                      {currentRoom.items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-muted rounded">
                          <div className="flex items-center gap-3">
                            {item.asset_template?.images?.[0]?.url && (
                              <img
                                src={item.asset_template.images[0].url}
                                alt=""
                                className="w-10 h-10 object-cover rounded"
                              />
                            )}
                            <div>
                              <p className="font-medium">{item.asset_template?.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {item.quantity}× @ {formatCurrency(item.unit_price)}/mo × {rentalLengthMonths}mo = {formatCurrency(item.total_price * rentalLengthMonths)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => onRemoveItem(currentRoom.id, item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-4" />
                  </div>
                )}

                {/* Manual Selection Mode */}
                {selectionMode === 'manual' && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">
                        Add Furniture
                        <span className="text-muted-foreground font-normal">
                          {' '}({furniture.length} for {getRoomTypeLabel(currentRoom.room_type)})
                        </span>
                      </p>
                    </div>
                    <Input
                      placeholder="Search furniture or categories..."
                      value={furnitureSearch}
                      onChange={(e) => setFurnitureSearch(e.target.value)}
                      className="mb-2"
                    />
                    <div
                      ref={furnitureContainerRef}
                      className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto"
                    >
                      {furniture.length > 0 ? (
                        <>
                          {furniture.map((template) => (
                            <button
                              key={template.id}
                              className="flex items-center gap-3 p-2 border rounded hover:bg-muted text-left"
                              onClick={() => onAddItem(currentRoom.id, template.id)}
                            >
                              <div className="w-16 h-16 flex-shrink-0 bg-muted rounded overflow-hidden">
                                {template.images?.[0]?.url ? (
                                  <img
                                    src={template.images[0].url}
                                    alt=""
                                    className="w-full h-full object-contain"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                    No image
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{template.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatCurrency(template.base_price || 0)}
                                </p>
                              </div>
                            </button>
                          ))}
                          {/* Infinite scroll trigger */}
                          <div ref={loadMoreRef} className="col-span-2 py-2 text-center">
                            {loadingFurniture && (
                              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-xs">Loading more...</span>
                              </div>
                            )}
                            {!hasMoreFurniture && furniture.length > 0 && (
                              <span className="text-xs text-muted-foreground">All items loaded</span>
                            )}
                          </div>
                        </>
                      ) : loadingFurniture ? (
                        <div className="col-span-2 flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      ) : (
                        <p className="col-span-2 text-center text-muted-foreground py-4">
                          No furniture found{furnitureSearch ? ' for this search' : ' for this room type'}.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Auto-Select Mode */}
                {selectionMode === 'auto' && (
                  <div className="space-y-4">
                    {/* Preferences */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                      <div>
                        <Label className="text-xs text-muted-foreground">Style Preference</Label>
                        <Select value={autoStylePreference} onValueChange={setAutoStylePreference}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="modern">Modern</SelectItem>
                            <SelectItem value="minimalist">Minimalist</SelectItem>
                            <SelectItem value="scandinavian">Scandinavian</SelectItem>
                            <SelectItem value="industrial">Industrial</SelectItem>
                            <SelectItem value="traditional">Traditional</SelectItem>
                            <SelectItem value="bohemian">Bohemian</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Priority</Label>
                        <Select value={autoPriority} onValueChange={setAutoPriority}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="best_match">Best Style Match</SelectItem>
                            <SelectItem value="maximize_items">Maximize Items in Budget</SelectItem>
                            <SelectItem value="minimum_cost">Minimum Cost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button
                      onClick={handleGenerateSuggestions}
                      disabled={loadingRecommendations || !currentRoom.budget}
                      className="w-full"
                    >
                      {loadingRecommendations ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating Suggestions...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          Generate Suggestions
                        </>
                      )}
                    </Button>

                    {!currentRoom.budget && (
                      <p className="text-sm text-amber-600 text-center">
                        Set a budget above to get recommendations
                      </p>
                    )}

                    {/* Package Selection */}
                    {packages && packages.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex gap-2 justify-center">
                          {packages.map((pkg) => (
                            <Button
                              key={pkg.tier}
                              variant={selectedPackageTier === pkg.tier ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setSelectedPackageTier(pkg.tier)}
                            >
                              {pkg.tier.charAt(0).toUpperCase() + pkg.tier.slice(1)} {formatCurrency(pkg.total_price)}
                            </Button>
                          ))}
                        </div>

                        {/* Selected Package Items */}
                        {selectedPackage && (
                          <div className="border rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-center">
                              <p className="font-medium">
                                {selectedPackageTier.charAt(0).toUpperCase() + selectedPackageTier.slice(1)} Package
                              </p>
                              <Badge>{formatCurrency(selectedPackage.total_price)}</Badge>
                            </div>
                            <div className="space-y-2">
                              {selectedPackage.items.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-2 bg-muted rounded">
                                  {item.asset_template?.images?.[0]?.url && (
                                    <img
                                      src={item.asset_template.images[0].url}
                                      alt=""
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{item.asset_template?.name}</p>
                                    <p className="text-xs text-muted-foreground">{item.reasoning}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm">{item.suggested_quantity}×</p>
                                    <p className="text-xs text-muted-foreground">
                                      {formatCurrency(item.asset_template?.base_price || 0)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => onAcceptPackage(currentRoom.id, selectedPackage.items)}
                                className="flex-1"
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Accept Package
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setPackages(null);
                                  setSelectionMode('manual');
                                }}
                              >
                                Start Fresh
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Render Generation Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="h-5 w-5" />
                  AI Render Generation
                </CardTitle>
                <CardDescription>
                  Generate photorealistic renders of this room with your selected furniture
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Room Image Reference */}
                  <div>
                    <Label className="mb-2 block">Room Image (sent to AI)</Label>
                    <div className="border rounded-lg aspect-video bg-muted flex items-center justify-center overflow-hidden">
                      {currentRoom.cropped_image_url ? (
                        <img
                          src={currentRoom.cropped_image_url}
                          alt="Cropped room"
                          className="w-full h-full object-contain"
                        />
                      ) : currentFloorPlan?.image_url ? (
                        <div className="text-center text-muted-foreground p-4">
                          <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No room area defined</p>
                          <p className="text-xs">Use the Room Area section above to crop the room from the floor plan</p>
                        </div>
                      ) : (
                        <div className="text-center text-muted-foreground">
                          <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No floor plan uploaded</p>
                          <p className="text-xs">Upload in Step 2</p>
                        </div>
                      )}
                    </div>
                    {currentRoom.width_ft && currentRoom.length_ft && (
                      <p className="text-sm text-muted-foreground mt-2 text-center">
                        {currentRoom.width_ft} × {currentRoom.length_ft} ft
                      </p>
                    )}
                  </div>

                  {/* AI Render Preview */}
                  <div>
                    <Label className="mb-2 block">Generated Render</Label>
                    <div className="border rounded-lg aspect-video bg-muted flex items-center justify-center overflow-hidden relative">
                      {generatingRender && (
                        <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                          <div className="text-center">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                            <p className="text-sm font-medium">Generating render...</p>
                            <p className="text-xs text-muted-foreground">This may take 30-60 seconds</p>
                          </div>
                        </div>
                      )}
                      {currentRoom.selected_render?.image_url ? (
                        <img
                          src={currentRoom.selected_render.image_url}
                          alt="AI render"
                          className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setLightboxUrl(currentRoom.selected_render?.image_url || null)}
                        />
                      ) : currentRoom.renders?.[0]?.image_url ? (
                        <img
                          src={currentRoom.renders[0].image_url}
                          alt="AI render"
                          className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setLightboxUrl(currentRoom.renders?.[0]?.image_url || null)}
                        />
                      ) : generatingRender ||
                        (currentRoom.renders?.length &&
                          currentRoom.renders.some(
                            (r) =>
                              r.status === RenderGenerationStatus.PENDING ||
                              r.status === RenderGenerationStatus.PROCESSING
                          )) ? (
                        <div className="text-center text-muted-foreground">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                          <p className="text-sm">Generating render...</p>
                          <p className="text-xs">This may take 30-60 seconds</p>
                        </div>
                      ) : (
                        <div className="text-center text-muted-foreground">
                          <Palette className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No render yet</p>
                          <p className="text-xs">Add furniture and generate</p>
                        </div>
                      )}
                    </div>
                    {renderError && (
                      <p className="text-sm text-destructive mt-2">{renderError}</p>
                    )}
                  </div>
                </div>

                {/* Render Variations */}
                {currentRoom.renders && currentRoom.renders.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2 block">Variations ({currentRoom.renders.length})</Label>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {currentRoom.renders.map((render) => (
                        <div
                          key={render.id}
                          className={`relative flex-shrink-0 cursor-pointer rounded-lg overflow-hidden border-2 transition-colors ${
                            render.is_selected ? 'border-primary' : 'border-transparent hover:border-muted-foreground'
                          }`}
                          onClick={() => onSelectRender(currentRoom.id, render.id)}
                        >
                          {render.image_url ? (
                            <img
                              src={render.image_url}
                              alt="Variation"
                              className="w-20 h-20 object-cover"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-muted flex items-center justify-center">
                              {render.status === RenderGenerationStatus.PROCESSING ||
                               render.status === RenderGenerationStatus.PENDING ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : render.status === RenderGenerationStatus.FAILED ? (
                                <span className="text-xs text-destructive">Failed</span>
                              ) : (
                                <Image className="h-4 w-4 opacity-50" />
                              )}
                            </div>
                          )}
                          {render.is_selected && (
                            <div className="absolute top-1 right-1 bg-primary rounded-full p-0.5">
                              <Star className="h-3 w-3 text-primary-foreground fill-current" />
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute bottom-1 right-1 h-5 w-5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRender(currentRoom.id, render.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Separator className="my-4" />

                {/* Prompt Controls */}
                <div className="space-y-4">
                  <Label className="flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Render Settings
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Style</Label>
                      <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select style" />
                        </SelectTrigger>
                        <SelectContent>
                          {stylePresets.length > 0 ? (
                            stylePresets.map((preset) => (
                              <SelectItem key={preset.key} value={preset.key}>
                                {preset.label}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="modern">Modern</SelectItem>
                              <SelectItem value="minimalist">Minimalist</SelectItem>
                              <SelectItem value="scandinavian">Scandinavian</SelectItem>
                              <SelectItem value="industrial">Industrial</SelectItem>
                              <SelectItem value="traditional">Traditional</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Lighting</Label>
                      <Select value={selectedLighting} onValueChange={setSelectedLighting}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select lighting" />
                        </SelectTrigger>
                        <SelectContent>
                          {lightingPresets.length > 0 ? (
                            lightingPresets.map((preset) => (
                              <SelectItem key={preset.key} value={preset.key}>
                                {preset.label}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="natural">Natural Daylight</SelectItem>
                              <SelectItem value="warm">Warm Evening</SelectItem>
                              <SelectItem value="bright">Bright Studio</SelectItem>
                              <SelectItem value="moody">Moody</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Camera</Label>
                      <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select angle" />
                        </SelectTrigger>
                        <SelectContent>
                          {cameraPresets.length > 0 ? (
                            cameraPresets.map((preset) => (
                              <SelectItem key={preset.key} value={preset.key}>
                                {preset.label}
                              </SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="wide">Wide Angle</SelectItem>
                              <SelectItem value="eye_level">Eye Level</SelectItem>
                              <SelectItem value="corner">Corner View</SelectItem>
                              <SelectItem value="birds_eye">Bird's Eye</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Room Environment Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Floor Type</Label>
                      <Select value={selectedFloorType} onValueChange={setSelectedFloorType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select floor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hardwood">Hardwood</SelectItem>
                          <SelectItem value="marble">Marble</SelectItem>
                          <SelectItem value="tile">Tile</SelectItem>
                          <SelectItem value="carpet">Carpet</SelectItem>
                          <SelectItem value="concrete">Polished Concrete</SelectItem>
                          <SelectItem value="laminate">Laminate</SelectItem>
                          <SelectItem value="stone">Natural Stone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Wall Color</Label>
                      <Select value={selectedWallColor} onValueChange={setSelectedWallColor}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select color" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="white">White</SelectItem>
                          <SelectItem value="cream">Cream / Off-White</SelectItem>
                          <SelectItem value="light gray">Light Gray</SelectItem>
                          <SelectItem value="beige">Beige</SelectItem>
                          <SelectItem value="sage green">Sage Green</SelectItem>
                          <SelectItem value="navy blue">Navy Blue</SelectItem>
                          <SelectItem value="charcoal">Charcoal</SelectItem>
                          <SelectItem value="terracotta">Terracotta</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Custom Instructions (optional)</Label>
                    <Textarea
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      placeholder="Add specific details: 'Large windows on left wall with afternoon sun, include 2-3 potted plants near windows, warm inviting atmosphere...'"
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleGenerate}
                      disabled={generatingRender || !currentRoom.items?.length}
                      className="flex-1"
                    >
                      {generatingRender ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="h-4 w-4 mr-2" />
                          Generate Render
                        </>
                      )}
                    </Button>
                    {currentRoom.renders && currentRoom.renders.length > 0 && (
                      <Button variant="outline" onClick={handleGenerate} disabled={generatingRender}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        New Variation
                      </Button>
                    )}
                  </div>

                  {!currentRoom.items?.length && (
                    <p className="text-sm text-muted-foreground text-center">
                      Add furniture above to generate a render
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <p>Select a room to edit or add a new room</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Full-screen Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxUrl(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setLightboxUrl(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={lightboxUrl}
            alt="Render preview"
            className="max-w-[95vw] max-h-[95vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

interface ReviewStepProps {
  quote: Partial<Quote>;
  rooms: QuoteRoom[];
  rentalLengthMonths: number;
  monthlyTotal: number;
  totalPrice: number;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ quote, rooms, rentalLengthMonths, monthlyTotal, totalPrice }) => {
  const getRentalLabel = (months: number) =>
    RENTAL_LENGTH_OPTIONS.find((opt) => opt.value === months)?.label || `${months} months`;
  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Customer</CardDescription>
            <CardTitle className="text-xl">{quote.customer_name}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Rooms</CardDescription>
            <CardTitle className="text-xl">{rooms.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Rental Duration</CardDescription>
            <CardTitle className="text-xl">{getRentalLabel(quote.rental_length_months || 6)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Monthly / Total Value</CardDescription>
            <CardTitle className="text-xl">
              {formatCurrency(monthlyTotal)}/mo
            </CardTitle>
            <p className="text-sm text-muted-foreground">{formatCurrency(totalPrice)} total</p>
          </CardHeader>
        </Card>
      </div>

      {/* Room Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Rooms & Items</CardTitle>
        </CardHeader>
        <CardContent>
          {rooms.length > 0 ? (
            <div className="space-y-4">
              {rooms.map((room) => {
                const roomMonthly = room.items_total || 0;
                const roomTotal = roomMonthly * rentalLengthMonths;
                const itemCount = room.items?.length || 0;
                return (
                  <div key={room.id} className="border rounded p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">{room.room_name || getRoomTypeLabel(room.room_type)}</p>
                        <p className="text-sm text-muted-foreground">
                          {itemCount} {itemCount === 1 ? 'item' : 'items'}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline">{formatCurrency(roomTotal)}</Badge>
                        <p className="text-xs text-muted-foreground mt-1">{formatCurrency(roomMonthly)}/mo × {rentalLengthMonths}mo</p>
                      </div>
                    </div>
                    {room.items && room.items.length > 0 && (
                      <div className="text-sm text-muted-foreground">
                        {room.items.map((item) => (
                          <span key={item.id} className="mr-2">
                            {item.quantity}× {item.asset_template?.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground">No rooms added</p>
          )}
        </CardContent>
      </Card>

      {/* Budget Check */}
      {quote.total_budget && (
        <Card className={totalPrice > quote.total_budget ? 'border-red-500' : 'border-green-500'}>
          <CardContent className="py-4">
            <div className="flex justify-between items-center">
              <span>Budget: {formatCurrency(quote.total_budget)}</span>
              <span>Total: {formatCurrency(totalPrice)}</span>
              <Badge variant={totalPrice > quote.total_budget ? 'destructive' : 'default'}>
                {totalPrice > quote.total_budget
                  ? `Over by ${formatCurrency(totalPrice - quote.total_budget)}`
                  : `Under by ${formatCurrency(quote.total_budget - totalPrice)}`}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EditQuote;
